import { useState } from 'react';
import { Button, Alert, AlertTitle, AlertDescription } from '@chakra-ui/react';
import { EventBookingStatus, EventInfoStatus, ICalendlyEvent, IScheduledEvent } from 'types';
import { getFormattedDateRange } from '../../../../../utilities/helpers';
import { Modal, CalendlyWidget, BookingDetailsSection, IPrefill } from 'shared-components';

export interface IProps {
  interview: ICalendlyEvent;
  event: IScheduledEvent;
  studentDetails: IPrefill;
}

export const InterviewItem = (props: IProps) => {
  const { interview, event, studentDetails } = props;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const InterviewDescription = () => {
    switch (event.bookingStatus) {
      case EventBookingStatus.REQUESTED:
        if (event.infoStatus === EventInfoStatus.INVITED) {
          return (
            <>
              You have been invited to an interview. Use the button below to schedule an interview with your
              institution.
            </>
          );
        } else {
          return <>You are no longer required to attend this interview.</>;
        }
      case EventBookingStatus.BOOKED:
        const dateStr = getFormattedDateRange(event.startDate, event.endDate);

        return (
          <>
            Your interview is <b>Booked</b>
            {dateStr ? ` on ${dateStr}` : null}.
          </>
        );
      case EventBookingStatus.CANCELED:
        return <>You are no longer required to attend this interview.</>;
      default:
        return null;
    }
  };

  const InterviewButton = () => {
    switch (event.bookingStatus) {
      case EventBookingStatus.REQUESTED:
        if (event.infoStatus === EventInfoStatus.INVITED) {
          return (
            <Button size="lg" variant="solid" onClick={() => setIsModalOpen(true)} mt={4}>
              Book interview
            </Button>
          );
        } else {
          return null;
        }
      case EventBookingStatus.BOOKED:
        return (
          <Button size="lg" variant="solid" onClick={() => setIsModalOpen(true)} mt={4}>
            View event details
          </Button>
        );
      default:
        return null;
    }
  };

  const interviewColorScheme = () => {
    switch (event.bookingStatus) {
      case EventBookingStatus.BOOKED:
        return 'green';
      case EventBookingStatus.REQUESTED:
        return 'orange';
      case EventBookingStatus.CANCELED:
        return 'gray';
    }
  };

  const InterviewModal = () => {
    switch (event.bookingStatus) {
      case EventBookingStatus.REQUESTED:
        return (
          <CalendlyWidget
            url={interview.schedulingUrl + `?utm_content=${interview.utmKey ?? ''}`}
            prefill={studentDetails}
            onClose={() => setIsModalOpen(false)}
          />
        );
      case EventBookingStatus.BOOKED:
        return (
          <Modal
            id={'booking-details'}
            title={'Booking Information'}
            show={true}
            onClose={() => setIsModalOpen(false)}
            maxWidthPx={750}
            hideFooter={true}
          >
            <BookingDetailsSection interview={interview} event={event} />
          </Modal>
        );
      default:
        return null;
    }
  };
 
  return (
    <Alert
      data-selector={`interview-event-${interview.name}`}
      data-booking-status={event.bookingStatus}
      variant="status-block"
      colorScheme={interviewColorScheme()}
    >
      <AlertTitle as="h3">{interview.name}</AlertTitle>

      <AlertDescription>
        <InterviewDescription />
      </AlertDescription>

      <InterviewButton />

      {isModalOpen && <InterviewModal />}
    </Alert>
  );
};
